import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {

    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;

    if (!productId || !sectionId) {
      throw new Error('Product ID and a section ID are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        if (result.data?.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          morphSection(sectionId, result.data, 'hydration', { injectStylesheet: true });
          requestAnimationFrame(() => {
            document.dispatchEvent(
              new CustomEvent("swym:collections-loaded")
            );
          });
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });


    //   add to cart functionality


    setTimeout(function () {
      document.querySelectorAll('.resource-list .resource-list__item').forEach(item => {
        const card = item.querySelector('product-card');
        let variants = JSON.parse(card.querySelector(".plp-actions").getAttribute("data-variants"));
        let optionsArray = JSON.parse(card.querySelector(".plp-actions").getAttribute("data-options-array"));


        productUpdate(variants, optionsArray, card);

        // swatches and colors
        const swatches = card.querySelectorAll(".swatches-container .swatch-item");
        swatches.forEach(swatch => {
          swatch.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();

            swatches.forEach(swatchItem => swatchItem.style.outline = "none");
            swatch.style.outline = "1px solid #d8d7d1";
            swatch.style.borderRadius = "50%";

            let getVariants = JSON.parse(swatch.getAttribute("data-variants"));
            let getOptions = JSON.parse(swatch.getAttribute("data-options-array"));
            let productMedia = JSON.parse(swatch.getAttribute("data-product-media"));
            let productInfo = JSON.parse(swatch.getAttribute("data-product-info"));
            productInfo.badges = swatch.getAttribute("data-product-badge");
            productInfo.discount = swatch.getAttribute("data-product-discount");
            productUpdate(getVariants, getOptions, card);
            productImageAndInfoUpdate(card, productMedia, productInfo)
          })
        })

        function sizeItems(actionsContainer, card) {
          actionsContainer.querySelectorAll(".size-item").forEach(actionItem => {
            actionItem.addEventListener("click", (e) => {
              e.preventDefault();
              e.stopPropagation();
              actionsContainer.querySelectorAll(".size-item").forEach(item => item.classList.remove("active-size"))
              actionItem.classList.add("active-size");
              card.querySelector(".my-custom-wishlist-button").setAttribute(
                'data-variant-id',
                actionItem.getAttribute('data-variant-id')
              );
              document.dispatchEvent(new CustomEvent("swym:collections-loaded"));
            })
          })

          actionsContainer.querySelector(".add-to-bag").addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();

            let activeSize = actionsContainer.querySelector(".active-size");
            if (activeSize) {
              let variantId = activeSize.dataset.variantId;
              fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json'
                },
                body: JSON.stringify({
                  id: variantId,
                  quantity: 1
                })
              })
                .then(res => res.json())
                .then(cart => {
                  document.dispatchEvent(
                    new CustomEvent('cart:update', {
                      bubbles: true,
                      detail: {
                        data: {
                          itemCount: 1,
                          source: 'product-form-component'
                        }
                      }
                    })
                  );
                })
                .catch(err => {
                  console.error("Error:", err);
                });
            }
          })
        }

        function productUpdate(variants, optionsArray, card) {

          const sizeIndex = optionsArray.findIndex(opt =>
            opt.toLowerCase().includes('size')
          );

          if (sizeIndex === -1) return;

          const actionsContainer = card.querySelector(".plp-actions");

          actionsContainer.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopPropagation();
          });

          actionsContainer.querySelector(".plp-sizes")?.remove();
          actionsContainer.querySelector(".add-to-bag")?.remove();

          const sizeWrapper = document.createElement("div");
          sizeWrapper.classList.add("plp-sizes");


          let firstAvailableAssigned = false;

          variants.forEach(variant => {
            const size = variant[`option${sizeIndex + 1}`];
            const sizeElement = document.createElement("span");
            sizeElement.textContent = size;
            sizeElement.classList.add("size-item");
            sizeElement.dataset.variantId = variant.id;
            sizeElement.dataset.available = variant.available;

            if (variant.available && !firstAvailableAssigned) {
              sizeElement.classList.add("active-size");
              firstAvailableAssigned = true;
            }

            sizeWrapper.appendChild(sizeElement);
          })

          actionsContainer.appendChild(sizeWrapper);

          let addButton = document.createElement("button");
          addButton.classList.add("add-to-bag");
          addButton.textContent = "Add to Bag";

          actionsContainer.appendChild(addButton);

          sizeItems(actionsContainer, card);
        }

        function productImageAndInfoUpdate(card, productMedia, productInfo) {
          const slides = card.querySelectorAll(
            ".card-gallery slideshow-component slideshow-container slideshow-slides slideshow-slide"
          );

          for (let i = 0; i < 2; i++) {
            const img = slides[i]?.querySelector('.product-media img');
            if (img && productMedia[i]) {
              img.src = productMedia[i];
              img.srcset = productMedia[i];
            }
          }


          // For Product Title, Handle and Price update
          card.querySelector('a[ref="productTitleLink"] p[role="heading"]').textContent = productInfo.title;
          card.querySelector('a[ref="productTitleLink"]').setAttribute("href", `/products/${productInfo.handle}`);
          card.querySelector('a[ref="cardGalleryLink"]').setAttribute("href", `/products/${productInfo.handle}`);
          if (productInfo.compare_at_price > 0) {
            card.querySelector('product-price div[ref="priceContainer"] .price__regular').classList.add('price__hidden');
            card.querySelector('product-price div[ref="priceContainer"] .price__sale').classList.remove('price__hidden');
            card.querySelector('product-price div[ref="priceContainer"] .price__sale .price-item--sale.price').textContent = (productInfo.price / 100).toFixed(2);
            card.querySelector('product-price div[ref="priceContainer"] .price__sale .price-item--regular.compare-at-price').textContent = window.formatMoney(productInfo.compare_at_price);
          }
          else {
            card.querySelector('product-price div[ref="priceContainer"] .price__regular').classList.remove('price__hidden');
            card.querySelector('product-price div[ref="priceContainer"] .price__sale').classList.add('price__hidden');
            card.querySelector('product-price div[ref="priceContainer"] .price__regular .price').textContent = window.formatMoney(productInfo.price);
          }


          const garmentImage = card.querySelector('.product-media__garment_image');
          if (garmentImage) {
            const productMedia = garmentImage.closest('.product-media');
            if (productMedia.getAttribute('data-garment-image')) {
              if (productMedia.getAttribute('data-garment-image') == "-0") {
                garmentImage.src = productInfo.media[productInfo.media.length - 1].src
                garmentImage.srcset = productInfo.media[productInfo.media.length - 1].src
              } else if (productMedia.getAttribute('data-garment-image') == "-1") {
                garmentImage.src = productInfo.media[productInfo.media.length - 2].src
                garmentImage.srcset = productInfo.media[productInfo.media.length - 2].src
              } else {
                garmentImage.src = productInfo.media[productMedia.getAttribute('data-garment-image')].src
                garmentImage.srcset = productInfo.media[productMedia.getAttribute('data-garment-image')].src
              }
            }
          }

          let getProductBadges = card.querySelector(".product-badges");
          getProductBadges.innerHTML = ``;
          if (getProductBadges && productInfo.tags?.length) {
            let badgeText = '';

            if (productInfo.tags.includes('Final Sale')) {
              badgeText = 'Final Sale';
            } else if (productInfo.tags.includes('Sale')) {
              badgeText = 'Sale';
            }

            if (badgeText) {
              getProductBadges.innerHTML = `
      <div class="product-badges__badge product-badges__badge--rectangle sale-badge">
        ${badgeText}
      </div>
    `;
            }
          }
          if (productInfo.badges) {
            getProductBadges.innerHTML += `
      <div class="product-badges__badge product-badges__badge--rectangle sale-badge">
        ${productInfo.badges}
      </div>
    `;
          }

          let discountBadge = card.querySelector('product-price .discount-badge') || card.querySelector('.discount-badge');
          if (productInfo.discount) {
            if (!discountBadge) {
              discountBadge = document.createElement('div');
              discountBadge.className = 'discount-badge';
              const productPrice = card.querySelector('product-price');
              if (productPrice) {
                productPrice.appendChild(discountBadge);
              }
            }
            discountBadge.innerHTML = `<span>${productInfo.discount}</span>`;
            discountBadge.style.display = '';
          } else if (discountBadge) {
            discountBadge.innerHTML = '';
            discountBadge.style.display = 'none';
          }
          const wishlistButton = card.querySelector('.my-custom-wishlist-button');

          if (wishlistButton) {
            const selectedVariant = productInfo.variants.find(v => v.available) || productInfo.variants[0];

            wishlistButton.setAttribute('data-product-id', productInfo.id);
            wishlistButton.setAttribute('data-variant-id', selectedVariant.id);
            wishlistButton.setAttribute(
              'data-product-url',
              `${window.location.origin}/products/${productInfo.handle}`
            );
            wishlistButton.classList.forEach(className => {
              if (className.startsWith('product_')) {
                wishlistButton.classList.remove(className);
              }
            });
            wishlistButton.classList.add(`product_${productInfo.id}`);

            // Reset state so Swym can re-evaluate
            wishlistButton.classList.remove('swym-added');
            wishlistButton.setAttribute('aria-pressed', 'false');
            document.dispatchEvent(new CustomEvent("swym:collections-loaded"));

          }
        }




      })
    }, 3000)







  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
